import React, { useMemo, useState } from 'react';
import type { CampaignStats } from 'c-sdk';
import { PCAReportCountField, PCAReportMetric } from 'c-sdk';
import { AutoGrid } from 'c-components';
import { formatNumber, formatNumberWhole, secondsToHms } from 'c-lib';
import HoursMins from 'c-main/Components/Campaign/CampaignReports/HoursMins';
import CustomTooltip from 'c-main/Components/Campaign/CampaignReports/Views/ByField/CustomTooltip';
import { FieldReportType } from 'c-main/Components/Campaign/CampaignReports/types';
import type { ChartReportPart } from 'c-reports/Types';
import { ReportDateTimeframe, ReportType } from 'c-reports/Types';
import type { ReportControlsState } from 'c-reports/Components';
import { NetworkRequestState } from 'c-data-layer';
import { format, isValid, parse } from 'date-fns';
import { useDateUtils } from 'c-hooks';
import type { TypographyProps } from '@mui/material';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Button } from 'c-components';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import type { TranslationPath } from 'c-translation';
import { Translate, useCommonTranslation } from 'c-translation';
import type { SettledTarget } from 'c-main/Lib';
import { useResponsiveChartHeight } from './useResponsiveChartHeight';
import type { TopBoxConfig } from './summaryCustomizationConfig';
import { getTopBoxConfig } from './summaryCustomizationConfig';
import { resolveToDateHeadline } from './targetPacing';
import { StaticChartWidgetReport } from './summaryWidgetReports';

type Props = {
    stats: CampaignStats;
    overallStats?: CampaignStats | null;
    showVariance: boolean;
    auditedStats?: CampaignStats | null;
    averageImpressions?: number | null;
    playsTitle?: TranslationPath;
    isGeopathOnly?: boolean;
    campaignValues: { show: boolean; delivered: number; expected: number; currencySign: string };
    campaignTarget?: number;
    fullCampaignTarget?: number | null;
    /**
     * UL-8133 -- `vistar_us2` only: the headline target and % as given (see `targetHeadline` in
     * `targetPacing.ts`), never recomputed here. `'hidden'` under a filter targets can't follow.
     */
    targetHeadline?: SettledTarget | 'hidden' | null;
    isMediaOwner: boolean;
    isDsp?: boolean;
    isAverageAudienceMode?: boolean;
    isNonAuditedMode?: boolean;
    hideImpressionAnalysis?: boolean;
    nonAuditedImpressionsPercentage?: number | null;
    varianceContextLabel?: string;
    auditedVarianceLabel?: string | null;
    showAllAudiencesDeliveryOverview?: boolean;
    hasActiveFilters?: boolean;
    hasDateFilter?: boolean;
    overallSummaryDataDaysCount?: number;
    summaryDataDaysCount?: number;
    totalCampaignDays?: number;
    campaignEnded?: boolean;
    filteredEntityCount?: number | null;
    overallEntityCount?: number | null;
    filteredEntityLabel?: string | null;
    compact?: boolean;
    dailyCumulative?: DailyCumulativeRow[];
    overallDailyCumulative?: DailyCumulativeRow[];
    /** Per-box customisation for the compact top boxes, keyed by box key. */
    topBoxConfigs?: Record<string, TopBoxConfig>;
};

export type DailyCumulativeRow = {
    date: string;
    impacts: number;
    impactsExpected: number;
    impactsExpectedPaced?: number;
    impactsAudience?: number;
    impactsAverage?: number;
    plays: number;
    playsExpected: number;
    time: number;
    timeExpected: number;
};

const PCATotalsByMetric: React.FC<Props> = ({
    stats,
    overallStats = null,
    showVariance,
    campaignValues,
    isGeopathOnly,
    auditedStats,
    averageImpressions = null,
    playsTitle = 'Modules.Main.Campaigns.Overview.Analytics.playsTitle',
    campaignTarget,
    fullCampaignTarget = null,
    targetHeadline = null,
    isMediaOwner,
    isDsp = false,
    isAverageAudienceMode = false,
    isNonAuditedMode = false,
    hideImpressionAnalysis = false,
    nonAuditedImpressionsPercentage = null,
    varianceContextLabel,
    auditedVarianceLabel = null,
    showAllAudiencesDeliveryOverview = false,
    hasActiveFilters = false,
    hasDateFilter = false,
    overallSummaryDataDaysCount = 0,
    summaryDataDaysCount = 0,
    totalCampaignDays = 0,
    campaignEnded = false,
    filteredEntityCount = null,
    overallEntityCount = null,
    filteredEntityLabel = null,
    compact = false,
    dailyCumulative,
    overallDailyCumulative,
    topBoxConfigs,
}) => {
    const t = useCommonTranslation();

    if (compact) {
        if (isDsp) {
            return (
                <CompactDspTotalsRow
                    stats={stats}
                    overallStats={overallStats}
                    averageImpressions={averageImpressions}
                    isAverageAudienceMode={isAverageAudienceMode}
                    isNonAuditedMode={isNonAuditedMode}
                    isGeopathOnly={isGeopathOnly}
                    auditedVarianceLabel={auditedVarianceLabel}
                    campaignTarget={campaignTarget}
                    fullCampaignTarget={fullCampaignTarget}
                    targetHeadline={targetHeadline}
                    campaignValues={campaignValues}
                    hideImpressionAnalysis={hideImpressionAnalysis}
                    hasActiveFilters={hasActiveFilters}
                    hasDateFilter={hasDateFilter}
                    overallSummaryDataDaysCount={overallSummaryDataDaysCount}
                    summaryDataDaysCount={summaryDataDaysCount}
                    totalCampaignDays={totalCampaignDays}
                    campaignEnded={campaignEnded}
                    dailyCumulative={dailyCumulative}
                    overallDailyCumulative={overallDailyCumulative}
                    filteredEntityCount={filteredEntityCount}
                    overallEntityCount={overallEntityCount}
                    filteredEntityLabel={filteredEntityLabel}
                    topBoxConfigs={topBoxConfigs}
                />
            );
        }
        return (
            <CompactTotalsRow
                stats={stats}
                campaignValues={campaignValues}
                playsTitle={playsTitle}
                dailyCumulative={dailyCumulative}
                topBoxConfigs={topBoxConfigs}
            />
        );
    }

    if (showVariance) {
        return (
            <AutoGrid spacing={4} xs={12} lg={campaignValues.show && !isMediaOwner ? 3 : 4}>
                <TotalsTile
                    title="Modules.Main.Campaigns.Overview.Analytics.impactsTitle"
                    actual={stats.impacts}
                    expected={stats.impacts_expected}
                    performance={+stats.impacts_percentage}
                    inSchedule={stats.impacts_in_schedule}
                    outSchedule={stats.impacts_out_schedule}
                    unbooked={stats.impacts_unbooked}
                    showVariance={showVariance}
                />
                <TotalsTile
                    title={playsTitle}
                    actual={stats.plays}
                    expected={stats.plays_expected}
                    performance={+stats.plays_percentage}
                    inSchedule={stats.plays_in_schedule}
                    outSchedule={stats.plays_out_schedule}
                    unbooked={stats.plays_unbooked}
                    showVariance={showVariance}
                />
                <TotalsTile
                    title="Modules.Main.Campaigns.Overview.Analytics.timeTitle"
                    asTime
                    actual={stats.time}
                    expected={stats.time_expected}
                    performance={+stats.time_percentage}
                    inSchedule={stats.time_in_schedule}
                    outSchedule={stats.time_out_schedule}
                    unbooked={stats.time_unbooked}
                    showVariance={showVariance}
                />

                {campaignValues?.show && !isMediaOwner && (
                    <CampaignValueCard
                        expected={campaignValues.expected}
                        delivered={campaignValues.delivered}
                        currencySign={campaignValues.currencySign}
                    />
                )}
            </AutoGrid>
        );
    }

    const totalImpressions = stats?.impacts ?? 0;
    const overallResolvedStats = overallStats ?? stats;
    const overallImpressions = overallResolvedStats?.impacts ?? totalImpressions;
    const totalPlays = stats?.plays ?? 0;
    const overallPlays = overallResolvedStats?.plays ?? totalPlays;
    const comparisonPrimary = isAverageAudienceMode
        ? averageImpressions ?? 0
        : (auditedStats as any)?.geopath_audience ?? 0;
    const comparisonSecondary = isAverageAudienceMode
        ? totalImpressions
        : auditedStats?.impacts ?? 0;
    const comparisonPrimaryMultiplier = isAverageAudienceMode
        ? totalPlays > 0
            ? (averageImpressions ?? 0) / totalPlays
            : null
        : auditedStats?.plays
        ? ((auditedStats as any)?.geopath_audience ?? 0) / auditedStats.plays
        : null;
    const comparisonSecondaryDenominatorPlays = isAverageAudienceMode
        ? totalPlays
        : auditedStats?.plays ?? 0;
    const comparisonSecondaryMultiplier =
        comparisonSecondary != null && comparisonSecondaryDenominatorPlays > 0
            ? comparisonSecondary / comparisonSecondaryDenominatorPlays
            : null;
    const audienceVersionLabel =
        auditedVarianceLabel || t('Modules.Main.Campaigns.Overview.Analytics.audited');
    const auditedImpressionsPercentage =
        overallImpressions > 0 ? (comparisonSecondary / overallImpressions) * 100 : null;
    const dspVsAudienceVariance = comparisonPrimary - comparisonSecondary;
    const dspVsAudienceVariancePercentage =
        comparisonSecondary > 0 ? (comparisonPrimary / comparisonSecondary) * 100 : null;
    const dspVsAverageVariance =
        averageImpressions != null ? comparisonPrimary - comparisonSecondary : null;
    const dspVsAverageVariancePercentage =
        averageImpressions != null && comparisonSecondary > 0
            ? (comparisonPrimary / comparisonSecondary) * 100
            : null;

    const expectedImpressions = stats?.impacts_expected ?? 0;
    // What the whole campaign is expected to deliver, and what is left of it.
    // Both rows read from the same total so they cannot disagree, and remaining
    // is target minus delivered — positive while there is still delivery to come.
    const totalCampaignExpectedImpacts = fullCampaignTarget ?? expectedImpressions;
    const impactsRemaining = totalCampaignExpectedImpacts - totalImpressions;
    const geopathExpected = (stats as any)?.geopath_audience ?? null;

    const deliveryExpected = isGeopathOnly ? geopathExpected : expectedImpressions;

    const deliveryPerformance = campaignTarget
        ? (totalImpressions / campaignTarget) * 100
        : deliveryExpected > 0
        ? (totalImpressions / deliveryExpected) * 100
        : null;

    const deliveryVariance = campaignTarget
        ? totalImpressions - campaignTarget
        : deliveryExpected != null
        ? totalImpressions - deliveryExpected
        : null;
    const overallTargetImpressions = fullCampaignTarget ?? campaignTarget ?? expectedImpressions;
    const toDateTargetImpressions = campaignTarget || expectedImpressions;
    const overallTargetAchieved =
        overallTargetImpressions > 0 ? (overallImpressions / overallTargetImpressions) * 100 : null;
    const toDateTargetAchieved =
        toDateTargetImpressions > 0 ? (totalImpressions / toDateTargetImpressions) * 100 : null;
    const overallDeliveryVariance =
        overallTargetImpressions != null ? overallImpressions - overallTargetImpressions : null;
    const toDateDeliveryVariance =
        toDateTargetImpressions != null ? totalImpressions - toDateTargetImpressions : null;
    const filteredTotalImpressionsPercentage =
        overallImpressions > 0 ? (totalImpressions / overallImpressions) * 100 : null;
    const unfilteredImpressionsDifference = overallImpressions - totalImpressions;
    const cardSx = {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    } as const;

    const cardContentSx = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    } as const;
    const resolvedVarianceContextLabel = isAverageAudienceMode
        ? varianceContextLabel
        : auditedVarianceLabel || varianceContextLabel;
    const totalImpactsTitle = t(
        isDsp
            ? 'Modules.Main.Campaigns.Overview.Analytics.totalDeliveredImpacts'
            : 'Modules.Main.Campaigns.Overview.Analytics.totalImpacts',
    );
    const variancePercTitleText = resolvedVarianceContextLabel
        ? t('Modules.Main.Campaigns.Overview.Analytics.variancePercWithContext', {
              value: resolvedVarianceContextLabel,
          })
        : t('Modules.Main.Campaigns.Overview.Analytics.variancePerc');
    const varianceTitleText = resolvedVarianceContextLabel
        ? t('Modules.Main.Campaigns.Overview.Analytics.varianceWithContext', {
              value: resolvedVarianceContextLabel,
          })
        : t('Modules.Main.Campaigns.Overview.Analytics.variance');
    const deliveryTitle = isGeopathOnly
        ? t('Modules.Main.Campaigns.Overview.Analytics.deliverySummary')
        : t('Modules.Main.Campaigns.Overview.Analytics.deliveryOfTarget');
    const renderDeliveryOverviewCard = (
        titleText: React.ReactNode,
        performanceTitleText: React.ReactNode,
        deliveredTitleText: React.ReactNode,
        targetTitleText: React.ReactNode,
        varianceTitleText: React.ReactNode,
        targetValue: number | null,
        performanceValue: number | null,
        deliveredValue: number | null,
        varianceValue: number | null,
        campaignDaysValueText: string | null,
        showVariancePlusMinus = true,
        isDimmed = false,
        showSpacerRow = false,
        secondaryFooterTitleText: React.ReactNode = null,
        secondaryFooterValue: number | null = null,
        footerTitleText: React.ReactNode = t(
            'Modules.Main.Campaigns.Overview.Analytics.campaignDays',
        ),
    ) => (
        <Card
            sx={{
                ...cardSx,
                bgcolor: isDimmed ? 'action.hover' : undefined,
            }}
        >
            <CardContent sx={cardContentSx}>
                <Typography variant="h4" color="textPrimary">
                    {titleText}
                </Typography>
                <Divider sx={{ borderBottomWidth: 2, my: 1 }} />

                <AutoGrid spacing={0} xs={12}>
                    <FormatCount
                        asTime={false}
                        count={performanceValue}
                        countSuffix={performanceValue != null ? '%' : ''}
                        asWholeNumber={false}
                        titleText={performanceTitleText}
                        countVariant="h4"
                    />
                    <FormatCount
                        asTime={false}
                        count={deliveredValue}
                        titleText={deliveredTitleText}
                    />
                    <FormatCount asTime={false} count={targetValue} titleText={targetTitleText} />
                    <FormatCount
                        asTime={false}
                        count={varianceValue}
                        asWholeNumber
                        showPlusMinus={showVariancePlusMinus}
                        titleText={varianceTitleText}
                    />

                    {showSpacerRow && <Box minHeight={26.5} />}

                    <Divider sx={{ borderColor: 'grey.300', mt: 1, mb: 1 }} />

                    {secondaryFooterTitleText && (
                        <FormatCount
                            asTime={false}
                            count={secondaryFooterValue}
                            titleText={secondaryFooterTitleText}
                        />
                    )}

                    <AutoGrid spacing={1} justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <Typography variant="subtitle1" color="textSecondary">
                                {footerTitleText}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Typography variant="subtitle1" color="textPrimary">
                                {campaignDaysValueText ?? '-'}
                            </Typography>
                        </Box>
                    </AutoGrid>
                </AutoGrid>
            </CardContent>
        </Card>
    );
    const renderDspMultiplierTitle = (tooltipText: string) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {t('Modules.Main.Campaigns.Overview.Analytics.averageDspImpressionMultiplier')}
            <Tooltip title={tooltipText} arrow>
                <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'help' }} />
            </Tooltip>
        </Box>
    );
    const renderFilterImpactsLabel = (filtered: boolean) => (
        <Box display="flex" alignItems="center" gap={0.5}>
            <Typography component="span" variant="inherit">
                {t('CommonWords.Impacts')}
            </Typography>
            {filtered ? (
                <FilterAltIcon sx={{ fontSize: 16 }} />
            ) : (
                <FilterAltOffIcon sx={{ fontSize: 16 }} />
            )}
        </Box>
    );
    const overallCampaignDaysText =
        totalCampaignDays > 0
            ? `${overallSummaryDataDaysCount || summaryDataDaysCount} of ${totalCampaignDays}`
            : null;
    const toDateCampaignDaysText =
        totalCampaignDays > 0 ? `${summaryDataDaysCount} of ${totalCampaignDays}` : null;
    const toDateDaysLabel = hasDateFilter
        ? t('Modules.Main.Campaigns.Overview.Analytics.campaignDays')
        : t('Modules.Main.Campaigns.Overview.Analytics.numberOfDaysLive');
    if (showAllAudiencesDeliveryOverview) {
        return (
            <AutoGrid spacing={4} xs={12} lg={6} alignItems="stretch">
                {renderDeliveryOverviewCard(
                    hasActiveFilters
                        ? t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewOverall')
                        : t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewOverall'),
                    t(
                        'Modules.Main.Campaigns.Overview.Analytics.percentageOfTotalTargetImpressions',
                    ),
                    t('Modules.Main.Campaigns.Overview.Analytics.deliveredImpressions'),
                    t('Modules.Main.Campaigns.Overview.Analytics.totalTargetImpressions'),
                    t('Modules.Main.Campaigns.Overview.Analytics.deliveryVariance'),
                    overallTargetImpressions,
                    overallTargetAchieved,
                    overallImpressions,
                    overallDeliveryVariance,
                    overallCampaignDaysText,
                    true,
                    hasActiveFilters,
                    false,
                    filteredEntityLabel,
                    overallEntityCount,
                )}
                {hasActiveFilters
                    ? renderDeliveryOverviewCard(
                          t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewToDate'),
                          t(
                              'Modules.Main.Campaigns.Overview.Analytics.percentageOfTotalImpressions',
                          ),
                          renderFilterImpactsLabel(true),
                          t('Modules.Main.Campaigns.Overview.Analytics.totalDeliveredImpressions'),
                          renderFilterImpactsLabel(false),
                          overallImpressions,
                          filteredTotalImpressionsPercentage,
                          totalImpressions,
                          unfilteredImpressionsDifference,
                          toDateCampaignDaysText,
                          false,
                          false,
                          false,
                          filteredEntityLabel,
                          filteredEntityCount,
                          toDateDaysLabel,
                      )
                    : renderDeliveryOverviewCard(
                          t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewToDate'),
                          t(
                              'Modules.Main.Campaigns.Overview.Analytics.percentageOfTargetImpressions',
                          ),
                          t('Modules.Main.Campaigns.Overview.Analytics.deliveredImpressions'),
                          t('Modules.Main.Campaigns.Overview.Analytics.expectedImpressionsToDate'),
                          t('Modules.Main.Campaigns.Overview.Analytics.deliveryVariance'),
                          toDateTargetImpressions,
                          toDateTargetAchieved,
                          totalImpressions,
                          toDateDeliveryVariance,
                          toDateCampaignDaysText,
                          true,
                          false,
                          false,
                          filteredEntityLabel,
                          filteredEntityCount,
                          toDateDaysLabel,
                      )}
            </AutoGrid>
        );
    }

    return (
        <AutoGrid spacing={4} xs={12} lg={hideImpressionAnalysis ? 12 : 6} alignItems="stretch">
            {isGeopathOnly || isAverageAudienceMode || isNonAuditedMode ? (
                renderDeliveryOverviewCard(
                    isNonAuditedMode
                        ? t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewOverall')
                        : t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewOverall'),
                    t(
                        'Modules.Main.Campaigns.Overview.Analytics.percentageOfTotalTargetImpressions',
                    ),
                    t('Modules.Main.Campaigns.Overview.Analytics.deliveredImpressions'),
                    t('Modules.Main.Campaigns.Overview.Analytics.totalTargetImpressions'),
                    t('Modules.Main.Campaigns.Overview.Analytics.deliveryVariance'),
                    overallTargetImpressions,
                    overallTargetAchieved,
                    overallImpressions,
                    overallDeliveryVariance,
                    overallCampaignDaysText,
                    true,
                    isAverageAudienceMode || isNonAuditedMode ? hasActiveFilters : true,
                    true,
                    t('Modules.Main.Campaigns.Overview.Analytics.totalPlays'),
                    overallPlays,
                )
            ) : (
                <Card sx={cardSx}>
                    <CardContent sx={cardContentSx}>
                        <Typography variant="h4" color="textPrimary">
                            {deliveryTitle}
                        </Typography>
                        <Divider sx={{ borderBottomWidth: 2, my: 1 }} />

                        <AutoGrid spacing={0} xs={12}>
                            <FormatCount
                                asTime={false}
                                count={deliveryPerformance}
                                countSuffix={deliveryPerformance != null ? '%' : ''}
                                asWholeNumber={false}
                                title="Modules.Main.Campaigns.Overview.Analytics.impactsPerformance"
                                countVariant="h4"
                            />

                            <FormatCount
                                asTime={false}
                                count={totalImpressions}
                                titleText={totalImpactsTitle}
                            />

                            <FormatCount
                                asTime={false}
                                count={campaignTarget || expectedImpressions}
                                title="Modules.Main.Campaigns.Overview.Analytics.expectedImpactsToDate"
                            />

                            <FormatCount
                                asTime={false}
                                count={deliveryVariance}
                                showPlusMinus
                                asWholeNumber
                                title="Modules.Main.Campaigns.Overview.Analytics.variance"
                            />

                            <Divider sx={{ borderColor: 'grey.300', my: 1 }} />

                            <FormatCount
                                asTime={false}
                                count={totalCampaignExpectedImpacts}
                                title="Modules.Main.Campaigns.Overview.Analytics.totalCampaignExpectedImpacts"
                            />

                            <FormatCount
                                asTime={false}
                                count={impactsRemaining}
                                showPlusMinus
                                title="Modules.Main.Campaigns.Overview.Analytics.impactsRemaining"
                            />

                            {hideImpressionAnalysis && nonAuditedImpressionsPercentage != null && (
                                <FormatCount
                                    asTime={false}
                                    asWholeNumber={false}
                                    count={nonAuditedImpressionsPercentage}
                                    countSuffix="%"
                                    titleText={t(
                                        'Modules.Main.Campaigns.Overview.Analytics.nonAuditedImpactsPercentage',
                                    )}
                                />
                            )}
                        </AutoGrid>
                    </CardContent>
                </Card>
            )}

            {!hideImpressionAnalysis && (
                <Card sx={cardSx}>
                    <CardContent sx={cardContentSx}>
                        <Typography variant="h4" color="textPrimary">
                            {isAverageAudienceMode ? (
                                <Translate path="Modules.Main.Campaigns.Overview.Analytics.averageFormatDeliveryOverview" />
                            ) : isNonAuditedMode ? (
                                <Translate path="Modules.Main.Campaigns.Overview.Analytics.nonAuditedDeliveryOverview" />
                            ) : (
                                t(
                                    'Modules.Main.Campaigns.Overview.Analytics.audienceDeliveryOverview',
                                    {
                                        value: audienceVersionLabel,
                                    },
                                )
                            )}
                        </Typography>
                        <Divider sx={{ borderBottomWidth: 2, my: 1 }} />

                        <AutoGrid spacing={0} xs={12}>
                            {isNonAuditedMode ? (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={nonAuditedImpressionsPercentage}
                                        countSuffix={
                                            nonAuditedImpressionsPercentage != null ? '%' : ''
                                        }
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.percentageOfCampaignImpressionsNonAudited"
                                        countVariant="h4"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={totalImpressions}
                                        title="Modules.Main.Campaigns.Overview.Analytics.deliveredNonAuditedImpressions"
                                    />

                                    <Box minHeight={26.5} />
                                    <Box minHeight={26.5} />
                                    <Box minHeight={26.5} />
                                </>
                            ) : isAverageAudienceMode ? (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={dspVsAverageVariancePercentage}
                                        countSuffix={
                                            dspVsAverageVariancePercentage != null ? '%' : ''
                                        }
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.dspVsAverageVariancePerc"
                                        countVariant="h4"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={comparisonSecondary}
                                        title="Modules.Main.Campaigns.Overview.Analytics.deliveredDspImpressions"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={comparisonPrimary}
                                        title="Modules.Main.Campaigns.Overview.Analytics.deliveredAverageImpressions"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={dspVsAverageVariance}
                                        showPlusMinus
                                        title="Modules.Main.Campaigns.Overview.Analytics.dspVsAverageVariance"
                                    />

                                    <Box minHeight={26.5} />
                                </>
                            ) : (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={auditedImpressionsPercentage}
                                        countSuffix={
                                            auditedImpressionsPercentage != null ? '%' : ''
                                        }
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.percentageOfCampaignImpressionsAudited"
                                        countVariant="h4"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={dspVsAudienceVariancePercentage}
                                        countSuffix={
                                            dspVsAudienceVariancePercentage != null ? '%' : ''
                                        }
                                        asWholeNumber={false}
                                        titleText={t(
                                            'Modules.Main.Campaigns.Overview.Analytics.dspVsAudienceVariancePerc',
                                            {
                                                value: audienceVersionLabel,
                                            },
                                        )}
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={comparisonSecondary}
                                        title="Modules.Main.Campaigns.Overview.Analytics.deliveredDspImpressions"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={comparisonPrimary}
                                        titleText={t(
                                            'Modules.Main.Campaigns.Overview.Analytics.deliveredAudienceImpressions',
                                            {
                                                value: audienceVersionLabel,
                                            },
                                        )}
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={dspVsAudienceVariance}
                                        showPlusMinus
                                        titleText={t(
                                            'Modules.Main.Campaigns.Overview.Analytics.dspVsAudienceVariance',
                                            {
                                                value: audienceVersionLabel,
                                            },
                                        )}
                                    />
                                </>
                            )}

                            <Divider sx={{ borderColor: 'grey.300', my: 1 }} />

                            {isNonAuditedMode ? (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={
                                            overallPlays > 0
                                                ? overallImpressions / overallPlays
                                                : null
                                        }
                                        asWholeNumber={false}
                                        titleText={renderDspMultiplierTitle(
                                            t(
                                                'Modules.Main.Campaigns.Overview.Analytics.averageDspImpressionMultiplierInfoAll',
                                            ),
                                        )}
                                    />
                                    <FormatCount
                                        asTime={false}
                                        count={
                                            totalPlays > 0 ? totalImpressions / totalPlays : null
                                        }
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.averageNonAuditedImpressionMultiplier"
                                    />
                                </>
                            ) : isAverageAudienceMode ? (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={
                                            overallPlays > 0
                                                ? overallImpressions / overallPlays
                                                : null
                                        }
                                        asWholeNumber={false}
                                        titleText={renderDspMultiplierTitle(
                                            t(
                                                'Modules.Main.Campaigns.Overview.Analytics.averageDspImpressionMultiplierInfoAll',
                                            ),
                                        )}
                                    />
                                    <FormatCount
                                        asTime={false}
                                        count={comparisonPrimaryMultiplier}
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.averageImpactsMultiplier"
                                    />
                                </>
                            ) : (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={comparisonSecondaryMultiplier}
                                        asWholeNumber={false}
                                        titleText={renderDspMultiplierTitle(
                                            t(
                                                'Modules.Main.Campaigns.Overview.Analytics.averageDspImpressionMultiplierInfoAudited',
                                            ),
                                        )}
                                    />
                                    <FormatCount
                                        asTime={false}
                                        count={comparisonPrimaryMultiplier}
                                        asWholeNumber={false}
                                        titleText={t(
                                            'Modules.Main.Campaigns.Overview.Analytics.averageAudienceImpressionMultiplier',
                                            {
                                                value: audienceVersionLabel,
                                            },
                                        )}
                                    />
                                </>
                            )}
                        </AutoGrid>
                    </CardContent>
                </Card>
            )}
        </AutoGrid>
    );
};

type TileProps = {
    title: TranslationPath;
    actual: number;
    expected: number;
    performance: number;

    inSchedule: number;
    outSchedule: number;
    unbooked: number;
    asTime?: boolean;
    showVariance?: boolean;
};

/** One metric's card, as the Summary shows it; exported for the Combined tab's impacts card. */
export const TotalsTile: React.FC<TileProps> = ({
    title,
    actual,
    expected,
    performance,
    asTime,
    inSchedule,
    outSchedule,
    unbooked,
    showVariance,
}) => {
    const performanceColour = useMemo(() => {
        if (expected <= 0) return undefined;
        return performance >= 100 ? 'success.main' : 'warning.main';
    }, [expected, performance]);
    const variance = useMemo(() => actual - expected, [actual, expected]);
    const varianceColour = useMemo(() => {
        if (actual - expected > 0) return 'success.main';
        if (actual - expected === 0) return null;
        return 'warning.main';
    }, [actual, expected]);

    return (
        <Card>
            <CardContent>
                <Typography variant="h4" color="textPrimary">
                    <Translate path={title} />
                </Typography>
                <Divider sx={{ borderBottomWidth: 2, my: 1 }} />
                <AutoGrid spacing={0} xs={12}>
                    <FormatCount
                        asTime={false}
                        count={expected > 0 ? performance : null}
                        countSuffix={expected > 0 ? '%' : ''}
                        title="Modules.Main.Campaigns.Overview.Analytics.performance"
                        countColor={performanceColour}
                        countVariant="h4"
                        asWholeNumber={false}
                    />
                    <FormatCount
                        count={expected}
                        asTime={asTime === true}
                        title="Modules.Main.Campaigns.Overview.Analytics.expected"
                    />
                    <FormatCount
                        count={actual}
                        asTime={asTime === true}
                        title="Modules.Main.Campaigns.Overview.Analytics.actual"
                    />
                    {showVariance && (
                        <FormatCount
                            count={variance}
                            asTime={asTime === true}
                            showPlusMinus
                            countColor={varianceColour}
                            title="Modules.Main.Campaigns.Overview.Analytics.variance"
                        />
                    )}
                    <FormatCount
                        count={inSchedule}
                        asTime={asTime === true}
                        title="Modules.Main.Campaigns.Overview.Analytics.inSchedule"
                        titleVariant="body2"
                        countVariant="body2"
                    />
                    <FormatCount
                        count={outSchedule}
                        asTime={asTime === true}
                        title="Modules.Main.Campaigns.Overview.Analytics.outSchedule"
                        titleVariant="body2"
                        countVariant="body2"
                    />
                    <FormatCount
                        asTime={asTime === true}
                        count={unbooked}
                        title="Modules.Main.Campaigns.Overview.Analytics.unbooked"
                        titleVariant="body2"
                        countVariant="body2"
                    />
                </AutoGrid>
            </CardContent>
        </Card>
    );
};

const CampaignValueCard: React.FC<{
    expected: number;
    delivered: number;
    currencySign: string;
}> = ({ expected, delivered, currencySign }) => {
    const currency = (n: number) => `${currencySign}${formatNumberWhole(Math.abs(n))}`;

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="h4" color="textPrimary">
                    <Translate path="Modules.Main.Campaigns.Overview.Analytics.valueTitle" />
                </Typography>
                <Divider sx={{ borderBottomWidth: 2, my: 1 }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="subtitle1" color="textSecondary">
                        <Translate path="Modules.Main.Campaigns.Overview.Analytics.expectedValue" />
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 600 }}>
                        {currency(expected)}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="subtitle1" color="textSecondary">
                        <Translate path="Modules.Main.Campaigns.Overview.Analytics.actualValueDelivered" />
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        color={
                            delivered > expected
                                ? 'success.main'
                                : expected > delivered
                                ? 'warning.main'
                                : null
                        }
                        sx={{ fontWeight: 600 }}
                    >
                        {currency(delivered)}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1" color="textSecondary">
                        <Translate path="Modules.Main.Campaigns.Overview.Analytics.variance" />
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        color={
                            delivered > expected
                                ? 'success.main'
                                : expected > delivered
                                ? 'warning.main'
                                : null
                        }
                        sx={{ fontWeight: 600 }}
                    >
                        {delivered < expected
                            ? `-${currency(delivered - expected)}`
                            : currency(delivered - expected)}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const FormatCount = React.memo<{
    count?: number;
    countSuffix?: React.ReactNode;
    asTime: boolean;
    asWholeNumber?: boolean;
    title?: TranslationPath;
    titleText?: React.ReactNode;
    titleColor?: TypographyProps['color'];
    titleVariant?: TypographyProps['variant'];
    showPlusMinus?: boolean;
    countColor?: TypographyProps['color'];
    countVariant?: TypographyProps['variant'];
}>(function FormatCount({
    count,
    countSuffix,
    asTime,
    asWholeNumber = true,
    title,
    titleText,
    showPlusMinus = false,
    titleColor = 'textSecondary',
    titleVariant = 'subtitle1',
    countColor = 'textPrimary',
    countVariant = 'subtitle1',
}) {
    const hasValue = count !== null && count !== undefined;
    const sign =
        showPlusMinus && hasValue
            ? (count as number) > 0
                ? '+'
                : (count as number) < 0
                ? '-'
                : ''
            : '';

    const absCount = hasValue ? Math.abs(count as number) : undefined;

    return (
        <AutoGrid spacing={1} justifyContent="space-between">
            <Box display="flex" alignItems="center">
                <Typography variant={titleVariant} color={titleColor}>
                    {titleText ?? (title ? <Translate path={title} /> : null)}
                </Typography>
            </Box>

            {asTime ? (
                <Box display="flex" alignItems="center">
                    {hasValue ? (
                        <>
                            {showPlusMinus && sign && (
                                <Typography
                                    variant={countVariant}
                                    color={countColor}
                                    sx={{ mr: 0.25 }}
                                >
                                    {sign}
                                </Typography>
                            )}
                            <HoursMins
                                color={countColor}
                                seconds={absCount}
                                variant={countVariant}
                            />
                        </>
                    ) : (
                        <Typography variant={countVariant} color={countColor}>
                            -
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box display="flex" alignItems="center">
                    <Typography variant={countVariant} color={countColor}>
                        {hasValue
                            ? `${sign}${
                                  asWholeNumber
                                      ? formatNumberWhole(absCount as number)
                                      : formatNumber(absCount as number)
                              }${countSuffix ?? ''}`
                            : '-'}
                    </Typography>
                </Box>
            )}
        </AutoGrid>
    );
});

const compactNumberFormatter = new Intl.NumberFormat('en-GB', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2,
});

const formatCompact = (value: number) =>
    Number.isFinite(value) ? compactNumberFormatter.format(value) : '0';

const formatHoursMinsShort = (seconds: number) => {
    const safe = Number.isFinite(seconds) ? seconds : 0;
    const sign = safe < 0 ? '-' : '';
    const hms = secondsToHms(Math.abs(safe));
    const parts: string[] = [];
    if (hms.h > 0) parts.push(`${hms.h} hrs`);
    parts.push(`${hms.m} mins`);
    return `${sign}${parts.join(' ')}`;
};

const CompactTile: React.FC<{
    title: string;
    valueCaption?: React.ReactNode;
    value: React.ReactNode;
    performance?: number | null;
    performanceSuffix?: string;
    performanceColored?: boolean;
    hasExpected?: boolean;
    expectedDisplay?: React.ReactNode;
    expectedLabel?: React.ReactNode;
    varianceDisplay?: React.ReactNode;
    varianceLabel?: React.ReactNode;
    varianceSign?: 'positive' | 'negative' | 'neutral';
    actualDisplay?: React.ReactNode;
    actualLabel?: React.ReactNode;
    sparklinePoints?: MetricChartPoint[];
    sparklineSwapColors?: boolean;
    chartActualLabel?: string;
    chartExpectedLabel?: string;
    chartHeight?: number;
    chartTooltipConfig?: MetricChartTooltipConfig;
    chartControls?: ReportControlsState;
    onChartControlsChange?: (controls: ReportControlsState) => void;
    onViewDetails?: () => void;
    infoTooltip?: React.ReactNode;
    disabled?: boolean;
    valueLabel?: React.ReactNode;
    performanceFirst?: boolean;
    /** Customisation: hide the inline chart. Defaults to shown. */
    showGraph?: boolean;
    /** Customisation: render the full breakdown (detailRows) inline instead of the summary rows. */
    showAllData?: boolean;
    detailRows?: MetricDetailsRow[];
}> = ({
    title,
    valueCaption,
    value,
    performance,
    performanceSuffix = 'vs expected',
    performanceColored = true,
    hasExpected = false,
    expectedDisplay,
    expectedLabel = 'Expected',
    varianceDisplay,
    varianceLabel = 'Variance',
    varianceSign = 'neutral',
    actualDisplay,
    actualLabel = 'Actual',
    sparklinePoints,
    sparklineSwapColors = false,
    chartActualLabel = 'Total Delivered Impressions',
    chartExpectedLabel = 'Expected Impressions',
    chartHeight = 200,
    chartTooltipConfig,
    chartControls,
    onChartControlsChange,
    onViewDetails,
    infoTooltip,
    disabled = false,
    valueLabel,
    performanceFirst = false,
    showGraph = true,
    showAllData = false,
    detailRows,
}) => {
    const theme = useTheme();
    const responsiveChartHeight = useResponsiveChartHeight(chartHeight);
    const tileChart =
        sparklinePoints && sparklinePoints.length > 1
            ? buildMetricReportChart(
                  sparklinePoints,
                  chartActualLabel,
                  chartExpectedLabel,
                  sparklineSwapColors,
                  chartTooltipConfig,
                  theme,
              )
            : null;
    const perfColour =
        !performanceColored || performance == null || !hasExpected
            ? 'text.primary'
            : performance >= 100
            ? 'success.main'
            : 'warning.main';
    const varianceColour =
        varianceSign === 'positive'
            ? 'success.main'
            : varianceSign === 'negative'
            ? 'warning.main'
            : 'text.primary';
    const metricRows: {
        key: string;
        label: React.ReactNode;
        value: React.ReactNode;
        color?: string;
    }[] = [];
    if (performanceFirst && hasExpected && performance != null) {
        metricRows.push({
            key: 'performance',
            label: 'Performance',
            value: `${Math.round(performance)}%`,
            color: perfColour,
        });
        metricRows.push({ key: 'value', label: valueLabel ?? 'Total', value });
    } else {
        metricRows.push({ key: 'value', label: valueCaption ?? valueLabel ?? '', value });
        if (hasExpected && performance != null) {
            metricRows.push({
                key: 'performance',
                label: performanceSuffix,
                value: `${Math.round(performance)}%`,
                color: perfColour,
            });
        }
    }
    if (expectedDisplay != null) {
        metricRows.push({ key: 'expected', label: expectedLabel, value: expectedDisplay });
    }
    if (actualDisplay != null) {
        metricRows.push({ key: 'actual', label: actualLabel, value: actualDisplay });
    }
    if (varianceDisplay != null) {
        metricRows.push({
            key: 'variance',
            label: varianceLabel,
            value: varianceDisplay,
            color: varianceColour,
        });
    }
    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...(disabled
                    ? {
                          opacity: 0.75,
                          bgcolor: 'action.disabledBackground',
                      }
                    : null),
            }}
        >
            <CardContent
                sx={{
                    py: 1.5,
                    px: 2,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    '&:last-child': { pb: 1 },
                }}
            >
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            lineHeight: 1.2,
                        }}
                    >
                        {title}
                    </Typography>
                    {infoTooltip && (
                        <Tooltip title={infoTooltip} arrow>
                            <InfoOutlinedIcon
                                fontSize="inherit"
                                sx={{ color: 'text.secondary', fontSize: '1rem' }}
                            />
                        </Tooltip>
                    )}
                </Stack>
                {showAllData && detailRows && detailRows.length > 0 ? (
                    <Box
                        sx={{
                            mt: 0.75,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1,
                        }}
                    >
                        {detailRows.map((row, idx) => {
                            const color =
                                row.tone && (row.kind === 'performance' || row.kind === 'variance')
                                    ? `${row.tone}.main`
                                    : undefined;
                            return (
                                <Stack
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`detail-${idx}`}
                                    alignItems="center"
                                    sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            lineHeight: 1.3,
                                            fontSize: '0.8rem',
                                            color,
                                        }}
                                    >
                                        {row.value}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.2, fontSize: '0.68rem' }}
                                    >
                                        {row.label}
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Box>
                ) : (
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={1}
                        sx={{ mt: 0.75 }}
                    >
                        {metricRows.map(r => (
                            <Stack
                                key={r.key}
                                alignItems="center"
                                sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}
                            >
                                <Typography
                                    variant="h6"
                                    noWrap
                                    sx={{
                                        fontWeight: 700,
                                        lineHeight: 1.3,
                                        color: r.color,
                                        maxWidth: '100%',
                                    }}
                                >
                                    {r.value}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.25 }}
                                >
                                    {r.label}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                )}
                {showGraph && tileChart && (
                    <Box sx={{ mt: 1.5 }}>
                        <StaticChartWidgetReport
                            widgetId="tile-chart"
                            namePrefix="pca_totals_tile"
                            reportType={ReportType.Line}
                            xAxisDataKey="date"
                            parts={tileChart.parts}
                            data={tileChart.data}
                            loadingState={NetworkRequestState.Success}
                            renderTooltipLabel={tileChart.renderTooltipLabel}
                            filterOptionLabels={{
                                expected: chartExpectedLabel,
                                delivered: chartActualLabel,
                                both: 'Both',
                            }}
                            defaultShowNumbers={chartTooltipConfig?.showPerformance ?? false}
                            hideControls
                            controls={chartControls}
                            onControlsChange={onChartControlsChange}
                            allowAccumulateToggle
                            height={responsiveChartHeight}
                        />
                    </Box>
                )}
            </CardContent>
            {onViewDetails && (
                <Box sx={{ px: 2, pb: 1, display: 'flex', justifyContent: 'center' }}>
                    <Button variant="text" size="small" onClick={onViewDetails}>
                        View details
                    </Button>
                </Box>
            )}
        </Card>
    );
};

const CompactPerformanceTile: React.FC<{ title: string; percent: number }> = ({
    title,
    percent,
}) => {
    const safePercent = Number.isFinite(percent) ? percent : 0;
    const clamped = Math.max(0, Math.min(safePercent, 100));
    const colour = safePercent >= 100 ? 'success.main' : 'warning.main';
    const statusLabel =
        safePercent > 100 ? 'Over Target' : safePercent === 100 ? 'On Target' : 'Under Target';
    const size = 130;
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent
                sx={{
                    py: 1.5,
                    px: 2,
                    '&:last-child': { pb: 1.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    height: '100%',
                }}
            >
                <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        lineHeight: 1.2,
                    }}
                >
                    {title}
                </Typography>
                <Box sx={{ position: 'relative', width: size, height: size }}>
                    <CircularProgress
                        variant="determinate"
                        value={100}
                        size={size}
                        thickness={5}
                        sx={{ color: 'grey.200', position: 'absolute', top: 0, left: 0 }}
                    />
                    <CircularProgress
                        variant="determinate"
                        value={clamped}
                        size={size}
                        thickness={5}
                        sx={{ color: colour, position: 'absolute', top: 0, left: 0 }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant="h4" sx={{ fontWeight: 700, color: colour }}>
                            {Math.round(safePercent)}%
                        </Typography>
                    </Box>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: colour, mt: 0.5 }}>
                    {statusLabel}
                </Typography>
            </CardContent>
        </Card>
    );
};

const PerfBadge: React.FC<{ percent: number; hasExpected: boolean }> = ({
    percent,
    hasExpected,
}) => {
    if (!hasExpected) return null;
    const colour = percent >= 100 ? 'success.main' : 'warning.main';
    return (
        <Box
            sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                bgcolor: colour,
                color: 'common.white',
                fontSize: '0.75rem',
                fontWeight: 700,
                lineHeight: 1.2,
            }}
        >
            {Math.round(percent)}%
        </Box>
    );
};

type TileMetric = 'impacts' | 'plays' | 'time' | 'spend';

type MetricDetailsRow = {
    label: React.ReactNode;
    value: React.ReactNode;
    kind?: 'performance' | 'variance' | 'normal';
    tone?: 'success' | 'warning' | 'info' | 'error';
};

type MetricChartPoint = { date: string; actual: number; expected: number };

type MetricChartTooltipConfig = {
    metric: PCAReportMetric;
    expectedLabel: string;
    percentageLabel: string;
    inScheduleLabel: string;
    outScheduleLabel: string;
    unbookedLabel: string;
    performanceLabel: string;
    countFields: PCAReportCountField[];
    hidePercentage: boolean;
    showPerformance: boolean;
    counts?: { inSchedule: number; outSchedule: number; unbooked: number };
};

const MetricChartTooltip: React.FC<{ tooltipProps: any; config: MetricChartTooltipConfig }> = ({
    tooltipProps,
    config,
}) =>
    tooltipProps?.active && tooltipProps?.payload?.length ? (
        <CustomTooltip
            tooltipProps={tooltipProps}
            expectedLabel={config.expectedLabel}
            percentageLabel={config.percentageLabel}
            inScheduleLabel={config.inScheduleLabel}
            outScheduleLabel={config.outScheduleLabel}
            unbookedLabel={config.unbookedLabel}
            timeFrame={ReportDateTimeframe.Daily}
            type={FieldReportType.OverTime}
            field={null as any}
            metric={config.metric}
            countFields={config.countFields}
            hidePercentage={config.hidePercentage}
            showPerformance={config.showPerformance}
            performanceLabel={config.performanceLabel}
        />
    ) : null;

const buildMetricChartParts = (
    actualKey: string,
    expectedKey: string,
    actualColor: string,
    expectedColor: string,
    swapColors: boolean,
    hasExpected: boolean,
): ChartReportPart[] => [
    // When swapped (audited / averages), the comparison series is the primary one and
    // total delivered is the grey dashed baseline, so the report controls (percent,
    // Expected/Delivered filter) treat comparison as "delivered" and total as "expected".
    ...(hasExpected
        ? [
              {
                  dataKey: expectedKey,
                  filterRole: swapColors ? 'delivered' : 'expected',
                  comparisonSeries: !swapColors,
                  lineProps: {
                      stroke: expectedColor,
                      strokeDasharray: swapColors ? undefined : '5 5',
                  },
                  barProps: { fill: expectedColor },
              } as ChartReportPart,
          ]
        : []),
    {
        dataKey: actualKey,
        filterRole: swapColors && hasExpected ? 'expected' : 'delivered',
        comparisonSeries: swapColors && hasExpected,
        lineProps: { stroke: actualColor, strokeDasharray: swapColors ? '5 5' : undefined },
        barProps: { fill: actualColor },
    } as ChartReportPart,
];

const metricChartPercentage = (point: MetricChartPoint, swapColors: boolean) => {
    const numerator = swapColors ? point.expected : point.actual;
    const denominator = swapColors ? point.actual : point.expected;
    return denominator > 0 ? (numerator / denominator) * 100 : 0;
};

const buildMetricReportChart = (
    points: MetricChartPoint[],
    actualLabel: string,
    expectedLabel: string,
    swapColors: boolean,
    tooltipConfig: MetricChartTooltipConfig | undefined,
    theme: any,
) => {
    const greyColor = theme.palette?.grey?.[500] ?? '#999';
    const primaryColor = theme.palette.primary.main;
    const actualColor = swapColors ? greyColor : primaryColor;
    const expectedColor = swapColors ? primaryColor : greyColor;
    const hasExpected = points.some(d => d.expected > 0);
    const data = points.map(d => {
        const row: Record<string, any> = {
            date: d.date,
            [actualLabel]: d.actual,
            [expectedLabel]: d.expected,
        };
        if (tooltipConfig) {
            row[tooltipConfig.percentageLabel] = metricChartPercentage(d, swapColors);
            if (tooltipConfig.counts) {
                row[tooltipConfig.inScheduleLabel] = tooltipConfig.counts.inSchedule;
                row[tooltipConfig.outScheduleLabel] = tooltipConfig.counts.outSchedule;
                row[tooltipConfig.unbookedLabel] = tooltipConfig.counts.unbooked;
            }
        }
        return row;
    });
    const parts = buildMetricChartParts(
        actualLabel,
        expectedLabel,
        actualColor,
        expectedColor,
        swapColors,
        hasExpected,
    );
    const renderTooltipLabel = tooltipConfig
        ? (p: any) => <MetricChartTooltip tooltipProps={p} config={tooltipConfig} />
        : undefined;
    return { data, parts, renderTooltipLabel };
};

const MetricDetailsChart: React.FC<{
    data: MetricChartPoint[];
    valueFormatter?: (n: number) => string;
    actualLabel?: string;
    expectedLabel?: string;
    swapColors?: boolean;
    height?: number;
    tooltipConfig?: MetricChartTooltipConfig;
}> = ({
    data,
    valueFormatter,
    actualLabel = 'Total Delivered Impressions',
    expectedLabel = 'Expected Impressions',
    swapColors = false,
    height = 360,
    tooltipConfig,
}) => {
    const theme = useTheme();
    const { formatDateString } = useDateUtils();
    const yFmt = valueFormatter ?? (n => formatCompact(n));
    const chartData = useMemo(() => {
        if (!data) return [];
        return data.map(d => {
            const parsed = parse(d.date, 'yyyy-MM-dd', new Date());
            const display = isValid(parsed)
                ? (formatDateString({ date: parsed }) as string) || format(parsed, 'dd-MMM-yyyy')
                : d.date;
            const row: Record<string, any> = {
                date: display,
                [actualLabel]: d.actual,
                [expectedLabel]: d.expected,
            };
            if (tooltipConfig) {
                row[tooltipConfig.percentageLabel] = metricChartPercentage(d, swapColors);
                if (tooltipConfig.counts) {
                    row[tooltipConfig.inScheduleLabel] = tooltipConfig.counts.inSchedule;
                    row[tooltipConfig.outScheduleLabel] = tooltipConfig.counts.outSchedule;
                    row[tooltipConfig.unbookedLabel] = tooltipConfig.counts.unbooked;
                }
            }
            return row;
        });
    }, [data, formatDateString, actualLabel, expectedLabel, swapColors, tooltipConfig]);
    if (!data || data.length < 2) return null;
    const hasExpected = data.some(d => d.expected > 0);
    const greyColor = (theme.palette as any).grey?.[500] ?? '#999';
    const primaryColor = theme.palette.primary.main;
    const expectedColor = swapColors ? primaryColor : greyColor;
    const actualColor = swapColors ? greyColor : primaryColor;
    const tooltipFmt = valueFormatter ?? ((n: number) => formatNumberWhole(n));
    const renderTooltip = (tProps: any) => {
        if (!tProps?.active || !tProps?.payload?.length) return null;
        return (
            <Paper elevation={4} sx={{ p: 2 }}>
                <Box maxWidth="25em">
                    <Typography variant="subtitle2" noWrap>
                        {tProps.label}
                    </Typography>
                </Box>
                <Box>
                    {tProps.payload.map((p: any) => (
                        <Box key={p.dataKey} sx={{ color: p.color }}>
                            <Typography variant="body1">
                                {p.name}: {tooltipFmt(Number(p.value))}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>
        );
    };
    return (
        <Box sx={{ width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 25, right: 50, left: 40, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="date"
                        tickMargin={10}
                        interval="equidistantPreserveStart"
                        tick={(props: any) => {
                            const { x, y, payload } = props;
                            const text = String(payload?.value ?? '').trim();
                            const dateRegex = /^\d{2}-[A-Za-z]{3}-\d{4}$/;
                            let lines: string[] = [text];
                            if (dateRegex.test(text)) {
                                const [day, month, year] = text.split('-');
                                lines = [`${day}-${month}`, year];
                            }
                            return (
                                <text x={x} y={y} textAnchor="middle" fontSize={14} fill="#666">
                                    {lines.map((line, idx) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <tspan key={idx} x={x} dy={idx === 0 ? 0 : 16}>
                                            {line}
                                        </tspan>
                                    ))}
                                </text>
                            );
                        }}
                    />
                    <YAxis width={20} tickFormatter={yFmt as any} />
                    <RechartsTooltip
                        content={
                            tooltipConfig
                                ? (p: any) => (
                                      <MetricChartTooltip tooltipProps={p} config={tooltipConfig} />
                                  )
                                : renderTooltip
                        }
                    />
                    <Legend verticalAlign="top" align="right" wrapperStyle={{ top: 5 }} />
                    {hasExpected && (
                        <Line
                            type="monotone"
                            dataKey={expectedLabel}
                            name={expectedLabel}
                            stroke={expectedColor}
                            strokeDasharray={swapColors ? undefined : '5 5'}
                            strokeWidth={4}
                            dot={false}
                            activeDot={{ r: 8 }}
                        />
                    )}
                    <Line
                        type="monotone"
                        dataKey={actualLabel}
                        name={actualLabel}
                        stroke={actualColor}
                        strokeDasharray={swapColors ? '5 5' : undefined}
                        strokeWidth={4}
                        dot={false}
                        activeDot={{ r: 8 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};

const MetricDetailsDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    title: string;
    rows: MetricDetailsRow[];
    chartData?: MetricChartPoint[];
    chartActualLabel?: string;
    chartExpectedLabel?: string;
    chartSwapColors?: boolean;
    chartTooltipConfig?: MetricChartTooltipConfig;
    controls?: ReportControlsState;
    onControlsChange?: (controls: ReportControlsState) => void;
}> = ({
    open,
    onClose,
    title,
    rows,
    chartData,
    chartActualLabel = 'Total Delivered Impressions',
    chartExpectedLabel = 'Expected Impressions',
    chartSwapColors = false,
    chartTooltipConfig,
    controls,
    onControlsChange,
}) => {
    const theme = useTheme();
    const {
        data: reportData,
        parts,
        renderTooltipLabel,
    } = buildMetricReportChart(
        chartData ?? [],
        chartActualLabel,
        chartExpectedLabel,
        chartSwapColors,
        chartTooltipConfig,
        theme,
    );
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <Box sx={{ flex: 1, fontWeight: 700 }}>{title}</Box>
                <IconButton size="small" aria-label="Close" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Divider sx={{ mb: 1.5 }} />
                {chartData && chartData.length > 1 && (
                    <Box sx={{ mb: 2 }}>
                        <StaticChartWidgetReport
                            widgetId="metric-details-chart"
                            namePrefix="pca_totals_details"
                            reportType={ReportType.Line}
                            xAxisDataKey="date"
                            parts={parts}
                            data={reportData}
                            loadingState={NetworkRequestState.Success}
                            renderTooltipLabel={renderTooltipLabel}
                            defaultShowNumbers={chartTooltipConfig?.showPerformance ?? false}
                            filterOptionLabels={{
                                expected: chartExpectedLabel,
                                delivered: chartActualLabel,
                                both: 'Both',
                            }}
                            controls={controls}
                            onControlsChange={onControlsChange}
                            allowAccumulateToggle
                            height={360}
                        />
                    </Box>
                )}
                <Stack spacing={1}>
                    {rows.map((row, idx) => {
                        const valueSx: Record<string, any> = {};
                        if (row.kind === 'performance') {
                            valueSx.fontWeight = 700;
                        }
                        if (row.tone && (row.kind === 'performance' || row.kind === 'variance')) {
                            valueSx.color = `${row.tone}.main`;
                        }
                        return (
                            <Stack
                                // eslint-disable-next-line react/no-array-index-key
                                key={`row-${idx}`}
                                direction="row"
                                alignItems="baseline"
                                justifyContent="space-between"
                                gap={2}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {row.label}
                                </Typography>
                                <Typography variant="body2" sx={valueSx}>
                                    {row.value}
                                </Typography>
                            </Stack>
                        );
                    })}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

const perfTone = (pct: number): 'success' | 'warning' => (pct >= 100 ? 'success' : 'warning');

const varianceTone = (delta: number): 'success' | 'warning' | undefined =>
    delta > 0 ? 'success' : delta < 0 ? 'warning' : undefined;

type DspDetailMode = 'overall' | 'toDate' | 'audited' | 'average' | 'nonAudited';

const CompactDspTotalsRow: React.FC<{
    stats: CampaignStats;
    overallStats?: CampaignStats | null;
    averageImpressions?: number | null;
    isAverageAudienceMode?: boolean;
    isNonAuditedMode?: boolean;
    isGeopathOnly?: boolean;
    auditedVarianceLabel?: string | null;
    campaignTarget?: number;
    fullCampaignTarget?: number | null;
    targetHeadline?: SettledTarget | 'hidden' | null;
    campaignValues: Props['campaignValues'];
    hideImpressionAnalysis?: boolean;
    hasActiveFilters?: boolean;
    hasDateFilter?: boolean;
    overallSummaryDataDaysCount?: number;
    summaryDataDaysCount?: number;
    totalCampaignDays?: number;
    campaignEnded?: boolean;
    dailyCumulative?: DailyCumulativeRow[];
    overallDailyCumulative?: DailyCumulativeRow[];
    filteredEntityCount?: number | null;
    overallEntityCount?: number | null;
    filteredEntityLabel?: string | null;
    topBoxConfigs?: Record<string, TopBoxConfig>;
}> = ({
    stats,
    overallStats = null,
    averageImpressions = null,
    isAverageAudienceMode = false,
    isNonAuditedMode = false,
    isGeopathOnly = false,
    auditedVarianceLabel = null,
    campaignTarget,
    fullCampaignTarget = null,
    targetHeadline = null,
    campaignValues,
    hideImpressionAnalysis = false,
    hasActiveFilters = false,
    hasDateFilter = false,
    overallSummaryDataDaysCount = 0,
    summaryDataDaysCount = 0,
    totalCampaignDays = 0,
    campaignEnded = false,
    dailyCumulative,
    overallDailyCumulative,
    filteredEntityCount = null,
    overallEntityCount = null,
    filteredEntityLabel = null,
    topBoxConfigs,
}) => {
    const t = useCommonTranslation();
    const tr = (key: string, data?: Record<string, unknown>) =>
        t(`Modules.Main.Campaigns.Overview.Analytics.${key}` as TranslationPath, data);
    const audienceVersionLabel =
        auditedVarianceLabel || t('Modules.Main.Campaigns.Overview.Analytics.audited');

    const tooltipConfig: MetricChartTooltipConfig = {
        metric: PCAReportMetric.Impacts,
        expectedLabel: t('Modules.Main.Campaigns.Overview.Reports.impacts.expectedLabel'),
        percentageLabel: t('Modules.Main.Campaigns.Overview.Reports.impacts.percentageLabel'),
        inScheduleLabel: t('Modules.Main.Campaigns.Overview.Reports.inScheduleLabel'),
        outScheduleLabel: t('Modules.Main.Campaigns.Overview.Reports.outScheduleLabel'),
        unbookedLabel: t('Modules.Main.Campaigns.Overview.Reports.unbookedLabel'),
        performanceLabel: t('Modules.Main.Campaigns.Overview.Analytics.performance'),
        countFields: [],
        hidePercentage: true,
        showPerformance: true,
    };

    const totalImpressions = stats?.impacts ?? 0;
    const overallResolvedStats = overallStats ?? stats;
    const overallImpressions = overallResolvedStats?.impacts ?? totalImpressions;
    const totalPlays = stats?.plays ?? 0;
    const overallPlays = overallResolvedStats?.plays ?? totalPlays;
    const expectedImpressions = stats?.impacts_expected ?? 0;
    const geopathExpected = (stats as any)?.geopath_audience ?? null;
    const deliveryExpected = isGeopathOnly ? geopathExpected : expectedImpressions;
    const deliveryPerformance = campaignTarget
        ? (totalImpressions / campaignTarget) * 100
        : deliveryExpected > 0
        ? (totalImpressions / deliveryExpected) * 100
        : null;
    const deliveryTarget = campaignTarget || expectedImpressions;
    const deliveryVariance = deliveryTarget
        ? totalImpressions - deliveryTarget
        : deliveryExpected != null
        ? totalImpressions - deliveryExpected
        : null;
    const fullTarget = fullCampaignTarget ?? expectedImpressions;
    const impactsRemaining =
        fullCampaignTarget != null
            ? totalImpressions - fullCampaignTarget
            : expectedImpressions - totalImpressions;

    // Averages mode mirrors audited mode: the average (audience) figure is primary and
    // total DSP impressions are the baseline, so variance = average - DSP.
    const comparisonPrimary = isAverageAudienceMode
        ? averageImpressions ?? 0
        : (stats as any)?.geopath_audience ?? 0;
    const comparisonSecondary = totalImpressions;
    const dspImpressionsValue = comparisonSecondary;
    const audienceImpressionsValue = comparisonPrimary;
    const dspVariance = comparisonPrimary - comparisonSecondary;
    const dspVariancePct =
        comparisonSecondary > 0 ? (comparisonPrimary / comparisonSecondary) * 100 : null;
    const overallImpsForPct = overallImpressions > 0 ? overallImpressions : 1;
    const audienceCoveragePct = (totalImpressions / overallImpsForPct) * 100;

    const dspMultiplierDenominator = totalPlays;
    const dspMultiplier =
        comparisonSecondary != null && dspMultiplierDenominator > 0
            ? comparisonSecondary / dspMultiplierDenominator
            : null;
    const audienceMultiplier = totalPlays > 0 ? comparisonPrimary / totalPlays : null;

    const valueDelivered = campaignValues?.delivered ?? 0;
    const valueExpected = campaignValues?.expected ?? 0;
    const valuePerf = valueExpected > 0 ? (valueDelivered / valueExpected) * 100 : 0;

    const audienceLabel = isAverageAudienceMode ? 'Average format' : audienceVersionLabel;
    const overallDimmed = hasActiveFilters;
    const rightMode: 'toDate' | 'audited' | 'average' | 'nonAudited' = isAverageAudienceMode
        ? 'average'
        : isNonAuditedMode
        ? 'nonAudited'
        : hideImpressionAnalysis
        ? 'toDate'
        : 'audited';

    const overallTarget = fullCampaignTarget ?? expectedImpressions;
    const overallTargetAchieved =
        overallTarget > 0 ? (overallImpressions / overallTarget) * 100 : null;
    const overallVariance = overallTarget > 0 ? overallImpressions - overallTarget : null;
    const campaignDaysLabel =
        totalCampaignDays > 0
            ? `${overallSummaryDataDaysCount || summaryDataDaysCount} of ${totalCampaignDays}`
            : null;
    const toDateDaysLabel =
        totalCampaignDays > 0 ? `${summaryDataDaysCount} of ${totalCampaignDays}` : null;
    const filteredTotalImpressionsPercentage =
        overallImpressions > 0 ? (totalImpressions / overallImpressions) * 100 : null;
    const unfilteredImpressionsDifference = overallImpressions - totalImpressions;
    const filteredEntityValue =
        filteredEntityCount != null && filteredEntityLabel
            ? `${formatNumberWhole(filteredEntityCount)} ${filteredEntityLabel}`
            : null;
    const overallEntityValue =
        overallEntityCount != null && filteredEntityLabel
            ? `${formatNumberWhole(overallEntityCount)} ${filteredEntityLabel}`
            : null;
    const impressionsWithFilterIcon = (filtered: boolean) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {t('CommonWords.Impacts')}
            {filtered ? (
                <FilterAltIcon sx={{ fontSize: 14 }} />
            ) : (
                <FilterAltOffIcon sx={{ fontSize: 14 }} />
            )}
        </Box>
    );

    const overallTooltip = tr('cardTooltips.overall');
    const toDateUnfilteredTooltip = tr('cardTooltips.toDateUnfiltered');
    const toDateFilteredTooltip = tr('cardTooltips.toDateFiltered');
    const auditedTooltip = tr('cardTooltips.audited', { value: audienceVersionLabel });
    const averageTooltip = tr('cardTooltips.average');
    const nonAuditedTooltip = tr('cardTooltips.nonAudited');

    const rightTooltip =
        rightMode === 'toDate'
            ? hasActiveFilters
                ? toDateFilteredTooltip
                : toDateUnfilteredTooltip
            : rightMode === 'audited'
            ? auditedTooltip
            : rightMode === 'nonAudited'
            ? nonAuditedTooltip
            : averageTooltip;

    const [openDetail, setOpenDetail] = useState<DspDetailMode | 'value' | null>(null);
    const [chartControlsByKey, setChartControlsByKey] = useState<
        Record<string, ReportControlsState>
    >({});
    const controlsFor = (key: string): ReportControlsState => {
        // audited/average/nonAudited share the 'analysis' box config for their cumulative default.
        const logicalKey =
            key === 'audited' || key === 'average' || key === 'nonAudited' ? 'analysis' : key;
        return (
            chartControlsByKey[key] ?? {
                showPercent: false,
                showNumbers: true,
                viewFilter: 'Both',
                cumulative: getTopBoxConfig(topBoxConfigs, logicalKey).cumulative,
            }
        );
    };
    const onControlsFor = (key: string) => (c: ReportControlsState) =>
        setChartControlsByKey(s => ({ ...s, [key]: c }));

    // Match the inline card / main: expected-to-date is the passed campaign target
    // (campaignTarget || expectedImpressions), not a chart-derived value -- or, on vistar_us2, the
    // target headline exactly as passed (UL-8133).
    const toDateHeadline = resolveToDateHeadline({
        targetHeadline,
        campaignTarget,
        expectedImpressions,
        totalImpressions,
        hasActiveFilters,
    });
    const toDateExpected = toDateHeadline.expected ?? 0;
    const toDateShowsShare = toDateHeadline.mode === 'shareOfTotal';
    const buildChartData = (
        which: 'overall' | 'toDate' | 'audited' | 'average' | 'nonAudited',
    ): MetricChartPoint[] | undefined => {
        const source =
            which === 'overall' ? overallDailyCumulative ?? dailyCumulative : dailyCumulative;
        if (!source || source.length === 0) return undefined;
        const suppressToDateExpected =
            which === 'toDate' && (toDateShowsShare || targetHeadline === 'hidden');
        const points = source.map(d => {
            let expected = 0;
            if (which === 'overall') {
                expected = d.impactsExpectedPaced ?? d.impactsExpected ?? 0;
            } else if (which === 'audited') {
                expected = d.impactsAudience ?? 0;
            } else if (which === 'average') {
                expected = d.impactsAverage ?? 0;
            } else if (which === 'nonAudited') {
                expected = 0;
            } else if (suppressToDateExpected) {
                expected = 0;
            } else {
                expected = d.impactsExpectedPaced ?? d.impactsExpected ?? 0;
            }
            return { date: d.date, actual: d.impacts, expected };
        });
        // Show the natural paced expected line (unscaled). The overall series is built
        // from unfiltered daily stats so the left box ignores filters, and both boxes use
        // the same paced calculation so they are identical when no filter is applied.
        return points;
    };
    const toDateChartData = buildChartData('toDate');
    const toDatePerformance = toDateHeadline.performance;
    const toDateVariance =
        toDateHeadline.expected != null ? totalImpressions - toDateHeadline.expected : null;

    // The To Date box uses "Number of Days Live" unless a date filter is applied.
    const toDateDaysRowLabel = hasDateFilter ? tr('campaignDays') : tr('numberOfDaysLive');
    // Full breakdown rows per DSP box — shared by the inline "show all data" render and the dialog.
    const buildDspRows = (mode: DspDetailMode): MetricDetailsRow[] => {
        if (mode === 'overall') {
            return [
                {
                    label: tr('percentageOfTotalTargetImpressions'),
                    value:
                        overallTargetAchieved != null
                            ? `${Math.round(overallTargetAchieved)}%`
                            : '—',
                    kind: 'performance',
                    tone:
                        overallTargetAchieved != null ? perfTone(overallTargetAchieved) : undefined,
                },
                { label: tr('deliveredImpressions'), value: formatNumber(overallImpressions) },
                { label: tr('totalTargetImpressions'), value: formatNumber(overallTarget ?? 0) },
                {
                    label: tr('deliveryVariance'),
                    value:
                        overallVariance != null
                            ? `${overallVariance >= 0 ? '+' : ''}${formatNumber(overallVariance)}`
                            : '—',
                    kind: 'variance',
                    tone: overallVariance != null ? varianceTone(overallVariance) : undefined,
                },
                overallEntityValue
                    ? { label: filteredEntityLabel ?? tr('unitsLabel'), value: overallEntityValue }
                    : null,
                campaignDaysLabel ? { label: tr('campaignDays'), value: campaignDaysLabel } : null,
            ].filter(Boolean) as MetricDetailsRow[];
        }
        if (mode === 'toDate') {
            return toDateShowsShare
                ? ([
                      {
                          label: tr('percentageOfTotalImpressions'),
                          value:
                              filteredTotalImpressionsPercentage != null
                                  ? `${filteredTotalImpressionsPercentage.toFixed(2)}%`
                                  : '—',
                          kind: 'performance',
                      },
                      {
                          label: impressionsWithFilterIcon(true),
                          value: formatNumber(totalImpressions),
                      },
                      {
                          label: tr('totalDeliveredImpressions'),
                          value: formatNumber(overallImpressions),
                      },
                      {
                          label: impressionsWithFilterIcon(false),
                          value: formatNumber(unfilteredImpressionsDifference),
                      },
                      filteredEntityValue
                          ? {
                                label: filteredEntityLabel ?? tr('unitsLabel'),
                                value: filteredEntityValue,
                            }
                          : null,
                      toDateDaysLabel
                          ? { label: toDateDaysRowLabel, value: toDateDaysLabel }
                          : null,
                  ].filter(Boolean) as MetricDetailsRow[])
                : ([
                      {
                          label: tr('percentageOfTargetImpressions'),
                          value:
                              toDatePerformance != null ? `${Math.round(toDatePerformance)}%` : '—',
                          kind: 'performance',
                          tone: toDatePerformance != null ? perfTone(toDatePerformance) : undefined,
                      },
                      { label: tr('deliveredImpressions'), value: formatNumber(totalImpressions) },
                      {
                          label: tr('expectedImpressionsToDate'),
                          value: formatNumber(toDateExpected),
                      },
                      {
                          label: tr('deliveryVariance'),
                          value:
                              toDateVariance != null
                                  ? `${toDateVariance >= 0 ? '+' : ''}${formatNumber(
                                        toDateVariance,
                                    )}`
                                  : '—',
                          kind: 'variance',
                          tone: toDateVariance != null ? varianceTone(toDateVariance) : undefined,
                      },
                      filteredEntityValue
                          ? {
                                label: filteredEntityLabel ?? tr('unitsLabel'),
                                value: filteredEntityValue,
                            }
                          : null,
                      toDateDaysLabel
                          ? { label: toDateDaysRowLabel, value: toDateDaysLabel }
                          : null,
                  ].filter(Boolean) as MetricDetailsRow[]);
        }
        if (mode === 'audited') {
            const auditedCoverage = (totalImpressions / overallImpsForPct) * 100;
            return [
                {
                    label: tr('percentageOfCampaignImpressionsAudited'),
                    value: Number.isFinite(auditedCoverage)
                        ? `${auditedCoverage.toFixed(2)}%`
                        : '—',
                    kind: 'performance',
                },
                {
                    label: tr('dspVsAudienceVariancePerc', { value: audienceVersionLabel }),
                    value: dspVariancePct != null ? `${dspVariancePct.toFixed(2)}%` : '—',
                    kind: 'performance',
                },
                { label: tr('deliveredDspImpressions'), value: formatNumber(dspImpressionsValue) },
                {
                    label: tr('deliveredAudienceImpressions', { value: audienceVersionLabel }),
                    value: formatNumber(audienceImpressionsValue),
                },
                {
                    label: tr('dspVsAudienceVariance', { value: audienceVersionLabel }),
                    value: `${dspVariance >= 0 ? '+' : ''}${formatNumber(dspVariance)}`,
                    kind: 'variance',
                    tone: varianceTone(dspVariance),
                },
                {
                    label: tr('averageDspImpressionMultiplier'),
                    value: dspMultiplier != null ? dspMultiplier.toFixed(2) : '—',
                },
                {
                    label: tr('averageAudienceImpressionMultiplier', {
                        value: audienceVersionLabel,
                    }),
                    value: audienceMultiplier != null ? audienceMultiplier.toFixed(2) : '—',
                },
            ];
        }
        if (mode === 'average') {
            return [
                {
                    label: tr('dspVsAverageVariancePerc'),
                    value: dspVariancePct != null ? `${dspVariancePct.toFixed(2)}%` : '—',
                    kind: 'performance',
                },
                { label: tr('deliveredDspImpressions'), value: formatNumber(dspImpressionsValue) },
                {
                    label: tr('deliveredAverageImpressions'),
                    value: formatNumber(audienceImpressionsValue),
                },
                {
                    label: tr('dspVsAverageVariance'),
                    value: `${dspVariance >= 0 ? '+' : ''}${formatNumber(dspVariance)}`,
                    kind: 'variance',
                    tone: varianceTone(dspVariance),
                },
                {
                    label: tr('averageDspImpressionMultiplier'),
                    value: dspMultiplier != null ? dspMultiplier.toFixed(2) : '—',
                },
                {
                    label: tr('averageImpactsMultiplier'),
                    value: audienceMultiplier != null ? audienceMultiplier.toFixed(2) : '—',
                },
            ];
        }
        if (mode === 'nonAudited') {
            return [
                {
                    label: tr('percentageOfCampaignImpressionsNonAudited'),
                    value:
                        filteredTotalImpressionsPercentage != null
                            ? `${filteredTotalImpressionsPercentage.toFixed(2)}%`
                            : '—',
                    kind: 'performance',
                },
                {
                    label: tr('deliveredNonAuditedImpressions'),
                    value: formatNumber(totalImpressions),
                },
                {
                    label: tr('averageDspImpressionMultiplier'),
                    value: overallPlays > 0 ? (overallImpressions / overallPlays).toFixed(2) : '—',
                },
                {
                    label: tr('averageNonAuditedImpressionMultiplier'),
                    value: totalPlays > 0 ? (totalImpressions / totalPlays).toFixed(2) : '—',
                },
            ];
        }
        return [];
    };

    const detailDialog = (() => {
        if (openDetail == null) return null;
        const close = () => setOpenDetail(null);
        if (openDetail === 'overall') {
            const rows = buildDspRows('overall');
            return (
                <MetricDetailsDialog
                    open
                    onClose={close}
                    title={tr('deliveryOverviewOverall')}
                    rows={rows}
                    controls={controlsFor('overall')}
                    onControlsChange={onControlsFor('overall')}
                    chartData={buildChartData('overall')}
                    chartActualLabel={tr('totalDeliveredImpressions')}
                    chartTooltipConfig={tooltipConfig}
                    chartExpectedLabel={tr('expectedImpacts')}
                />
            );
        }
        if (openDetail === 'toDate') {
            const rows = buildDspRows('toDate');
            return (
                <MetricDetailsDialog
                    open
                    onClose={close}
                    title={tr('deliveryOverviewToDate')}
                    rows={rows}
                    controls={controlsFor('toDate')}
                    onControlsChange={onControlsFor('toDate')}
                    chartData={toDateChartData}
                    chartActualLabel={tr('totalDeliveredImpressions')}
                    chartTooltipConfig={tooltipConfig}
                    chartExpectedLabel={tr('expectedImpacts')}
                />
            );
        }
        if (openDetail === 'audited') {
            const rows = buildDspRows('audited');
            return (
                <MetricDetailsDialog
                    open
                    onClose={close}
                    title={tr('impressionAnalysis')}
                    rows={rows}
                    controls={controlsFor('audited')}
                    onControlsChange={onControlsFor('audited')}
                    chartData={buildChartData('audited')}
                    chartActualLabel={tr('totalDeliveredImpressions')}
                    chartTooltipConfig={tooltipConfig}
                    chartExpectedLabel={tr('audienceImpressions', { value: audienceVersionLabel })}
                    chartSwapColors
                />
            );
        }
        if (openDetail === 'average') {
            const rows = buildDspRows('average');
            return (
                <MetricDetailsDialog
                    open
                    onClose={close}
                    title={tr('averageFormatDeliveryOverview')}
                    rows={rows}
                    controls={controlsFor('average')}
                    onControlsChange={onControlsFor('average')}
                    chartData={buildChartData('average')}
                    chartActualLabel={tr('totalDeliveredImpressions')}
                    chartTooltipConfig={tooltipConfig}
                    chartExpectedLabel={tr('averageFormatImpressions')}
                    chartSwapColors
                />
            );
        }
        if (openDetail === 'nonAudited') {
            const rows = buildDspRows('nonAudited');
            return (
                <MetricDetailsDialog
                    open
                    onClose={close}
                    title={tr('nonAuditedDeliveryOverview')}
                    rows={rows}
                    controls={controlsFor('nonAudited')}
                    onControlsChange={onControlsFor('nonAudited')}
                    chartData={buildChartData('nonAudited')}
                    chartActualLabel={tr('totalDeliveredImpressions')}
                    chartTooltipConfig={tooltipConfig}
                />
            );
        }
        return null;
    })();

    // Per-box customisation lookups. The second box is either "to date" or the impression
    // "analysis" box (audited/average/nonAudited) depending on rightMode.
    const overallBox = getTopBoxConfig(topBoxConfigs, 'overall');
    const secondBoxKey = rightMode === 'toDate' ? 'toDate' : 'analysis';
    const secondBox = getTopBoxConfig(topBoxConfigs, secondBoxKey);
    const dspCols = Math.max(
        1,
        (overallBox.hidden ? 0 : 1) + (secondBox.hidden ? 0 : 1) + (campaignValues?.show ? 1 : 0),
    );

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: `repeat(${Math.min(2, dspCols)}, 1fr)`,
                    md: `repeat(${dspCols}, 1fr)`,
                },
            }}
        >
            {!overallBox.hidden && (
                <CompactTile
                    title={tr('deliveryOverviewOverall')}
                    valueCaption={tr('percentageOfTotalTargetImpressions')}
                    value={
                        overallTargetAchieved != null ? `${overallTargetAchieved.toFixed(2)}%` : '—'
                    }
                    hasExpected
                    expectedLabel={tr('deliveredImpressions')}
                    expectedDisplay={formatCompact(overallImpressions)}
                    actualLabel={tr('totalTarget')}
                    actualDisplay={overallTarget > 0 ? formatCompact(overallTarget) : null}
                    sparklinePoints={buildChartData('overall')}
                    chartActualLabel={tr('totalDeliveredImpressions')}
                    chartTooltipConfig={tooltipConfig}
                    chartExpectedLabel={tr('expectedImpacts')}
                    infoTooltip={overallTooltip}
                    disabled={overallDimmed}
                    chartControls={controlsFor('overall')}
                    onChartControlsChange={onControlsFor('overall')}
                    showGraph={overallBox.showGraph}
                    showAllData={overallBox.showAllData}
                    detailRows={buildDspRows('overall')}
                    onViewDetails={() => setOpenDetail('overall')}
                />
            )}
            {!secondBox.hidden &&
                (rightMode === 'toDate' && toDateShowsShare ? (
                    <CompactTile
                        title={tr('deliveryOverviewToDate')}
                        valueCaption={tr('percentageOfTotalImpressions')}
                        value={
                            filteredTotalImpressionsPercentage != null
                                ? `${filteredTotalImpressionsPercentage.toFixed(2)}%`
                                : '—'
                        }
                        hasExpected
                        expectedLabel={impressionsWithFilterIcon(true)}
                        expectedDisplay={formatCompact(totalImpressions)}
                        actualLabel={tr('totalDeliveredImpressions')}
                        actualDisplay={formatCompact(overallImpressions)}
                        varianceLabel={tr('expectedToDate')}
                        varianceDisplay={toDateExpected > 0 ? formatCompact(toDateExpected) : null}
                        varianceSign="neutral"
                        sparklinePoints={buildChartData('toDate')}
                        chartActualLabel={tr('totalDeliveredImpressions')}
                        chartTooltipConfig={tooltipConfig}
                        chartExpectedLabel={tr('expectedImpacts')}
                        infoTooltip={rightTooltip}
                        chartControls={controlsFor('toDate')}
                        onChartControlsChange={onControlsFor('toDate')}
                        showGraph={secondBox.showGraph}
                        showAllData={secondBox.showAllData}
                        detailRows={buildDspRows('toDate')}
                        onViewDetails={() => setOpenDetail('toDate')}
                    />
                ) : rightMode === 'toDate' ? (
                    <CompactTile
                        title={tr('deliveryOverviewToDate')}
                        valueCaption={tr('percentageOfTargetImpressions')}
                        value={toDatePerformance != null ? `${toDatePerformance.toFixed(2)}%` : '—'}
                        hasExpected
                        expectedLabel={tr('deliveredImpressions')}
                        expectedDisplay={formatCompact(totalImpressions)}
                        actualLabel={tr('expectedToDate')}
                        actualDisplay={toDateExpected > 0 ? formatCompact(toDateExpected) : null}
                        sparklinePoints={buildChartData('toDate')}
                        chartActualLabel={tr('totalDeliveredImpressions')}
                        chartTooltipConfig={tooltipConfig}
                        chartExpectedLabel={tr('expectedImpacts')}
                        infoTooltip={rightTooltip}
                        chartControls={controlsFor('toDate')}
                        onChartControlsChange={onControlsFor('toDate')}
                        showGraph={secondBox.showGraph}
                        showAllData={secondBox.showAllData}
                        detailRows={buildDspRows('toDate')}
                        onViewDetails={() => setOpenDetail('toDate')}
                    />
                ) : rightMode === 'audited' ? (
                    <CompactTile
                        title={tr('audienceDeliveryOverview', { value: audienceVersionLabel })}
                        valueCaption={tr('percentageOfCampaignImpressionsAudited')}
                        value={
                            Number.isFinite(audienceCoveragePct)
                                ? `${audienceCoveragePct.toFixed(2)}%`
                                : '—'
                        }
                        hasExpected
                        expectedLabel={tr('deliveredDspImpressions')}
                        expectedDisplay={formatCompact(dspImpressionsValue)}
                        actualLabel={tr('deliveredAudienceImpressions', {
                            value: audienceVersionLabel,
                        })}
                        actualDisplay={formatCompact(audienceImpressionsValue)}
                        sparklinePoints={buildChartData('audited')}
                        sparklineSwapColors
                        chartActualLabel={tr('totalDeliveredImpressions')}
                        chartTooltipConfig={tooltipConfig}
                        chartExpectedLabel={tr('audienceImpressions', {
                            value: audienceVersionLabel,
                        })}
                        infoTooltip={rightTooltip}
                        chartControls={controlsFor('audited')}
                        onChartControlsChange={onControlsFor('audited')}
                        showGraph={secondBox.showGraph}
                        showAllData={secondBox.showAllData}
                        detailRows={buildDspRows('audited')}
                        onViewDetails={() => setOpenDetail('audited')}
                    />
                ) : rightMode === 'nonAudited' ? (
                    <CompactTile
                        title={tr('nonAuditedDeliveryOverview')}
                        valueCaption={tr('percentageOfCampaignImpressionsNonAudited')}
                        value={
                            filteredTotalImpressionsPercentage != null
                                ? `${filteredTotalImpressionsPercentage.toFixed(2)}%`
                                : '—'
                        }
                        hasExpected
                        expectedLabel={tr('deliveredNonAuditedImpressions')}
                        expectedDisplay={formatCompact(totalImpressions)}
                        actualLabel={tr('averageDspImpressionMultiplier')}
                        actualDisplay={
                            overallPlays > 0 ? (overallImpressions / overallPlays).toFixed(2) : null
                        }
                        varianceLabel={tr('averageNonAuditedImpressionMultiplier')}
                        varianceDisplay={
                            totalPlays > 0 ? (totalImpressions / totalPlays).toFixed(2) : null
                        }
                        varianceSign="neutral"
                        sparklinePoints={buildChartData('nonAudited')}
                        chartActualLabel={tr('totalDeliveredImpressions')}
                        chartTooltipConfig={tooltipConfig}
                        infoTooltip={rightTooltip}
                        chartControls={controlsFor('nonAudited')}
                        onChartControlsChange={onControlsFor('nonAudited')}
                        showGraph={secondBox.showGraph}
                        showAllData={secondBox.showAllData}
                        detailRows={buildDspRows('nonAudited')}
                        onViewDetails={() => setOpenDetail('nonAudited')}
                    />
                ) : (
                    <CompactTile
                        title={tr('averageFormatDeliveryOverview')}
                        valueCaption={tr('dspVsAverageVariancePerc')}
                        value={dspVariancePct != null ? `${dspVariancePct.toFixed(2)}%` : '—'}
                        hasExpected
                        expectedLabel={tr('deliveredDspImpressions')}
                        expectedDisplay={formatCompact(dspImpressionsValue)}
                        actualLabel={tr('deliveredAverageImpressions')}
                        actualDisplay={formatCompact(audienceImpressionsValue)}
                        varianceLabel={tr('dspVsAverageVariance')}
                        varianceDisplay={`${dspVariance >= 0 ? '+' : ''}${formatCompact(
                            dspVariance,
                        )}`}
                        varianceSign={
                            varianceTone(dspVariance) === 'success' ? 'positive' : 'negative'
                        }
                        sparklinePoints={buildChartData('average')}
                        sparklineSwapColors
                        chartActualLabel={tr('totalDeliveredImpressions')}
                        chartTooltipConfig={tooltipConfig}
                        chartExpectedLabel={tr('averageFormatImpressions')}
                        infoTooltip={rightTooltip}
                        chartControls={controlsFor('average')}
                        onChartControlsChange={onControlsFor('average')}
                        showGraph={secondBox.showGraph}
                        showAllData={secondBox.showAllData}
                        detailRows={buildDspRows('average')}
                        onViewDetails={() => setOpenDetail('average')}
                    />
                ))}
            {campaignValues?.show && (
                <CompactTile
                    title={tr('valueTitle')}
                    value={`${campaignValues.currencySign}${formatCompact(valueDelivered)}`}
                    performance={valuePerf}
                    hasExpected={valueExpected > 0}
                    expectedLabel={tr('expectedValue')}
                    expectedDisplay={
                        valueExpected > 0
                            ? `${campaignValues.currencySign}${formatCompact(valueExpected)}`
                            : null
                    }
                    actualLabel={tr('actualValueDelivered')}
                    actualDisplay={`${campaignValues.currencySign}${formatCompact(valueDelivered)}`}
                    varianceDisplay={
                        valueExpected > 0
                            ? `${valueDelivered - valueExpected >= 0 ? '+' : ''}${
                                  campaignValues.currencySign
                              }${formatCompact(valueDelivered - valueExpected)}`
                            : null
                    }
                    varianceSign={
                        valueExpected > 0
                            ? valueDelivered - valueExpected > 0
                                ? 'positive'
                                : valueDelivered - valueExpected < 0
                                ? 'negative'
                                : 'neutral'
                            : 'neutral'
                    }
                />
            )}
            {detailDialog}
        </Box>
    );
};

const CompactTotalsRow: React.FC<{
    stats: CampaignStats;
    campaignValues: Props['campaignValues'];
    playsTitle: TranslationPath;
    dailyCumulative?: DailyCumulativeRow[];
    topBoxConfigs?: Record<string, TopBoxConfig>;
}> = ({ stats, campaignValues, playsTitle, dailyCumulative, topBoxConfigs }) => {
    const t = useCommonTranslation();
    const [openMetric, setOpenMetric] = useState<TileMetric | null>(null);
    const [chartControlsByKey, setChartControlsByKey] = useState<
        Record<string, ReportControlsState>
    >({});
    const controlsFor = (key: string): ReportControlsState =>
        chartControlsByKey[key] ?? {
            showPercent: false,
            showNumbers: false,
            viewFilter: 'Both',
            cumulative: getTopBoxConfig(topBoxConfigs, key).cumulative,
        };
    const onControlsFor = (key: string) => (c: ReportControlsState) =>
        setChartControlsByKey(s => ({ ...s, [key]: c }));
    const impactsPerf = Number(stats.impacts_percentage ?? 0);
    const playsPerf = Number(stats.plays_percentage ?? 0);
    const timePerf = Number(stats.time_percentage ?? 0);
    const impactsPoints = useMemo<MetricChartPoint[] | undefined>(
        () =>
            dailyCumulative?.map(d => ({
                date: d.date,
                actual: d.impacts,
                expected: d.impactsExpected,
            })),
        [dailyCumulative],
    );
    const playsPoints = useMemo<MetricChartPoint[] | undefined>(
        () =>
            dailyCumulative?.map(d => ({
                date: d.date,
                actual: d.plays,
                expected: d.playsExpected,
            })),
        [dailyCumulative],
    );
    const overallPerf = Number(
        stats.impacts_expected > 0 ? impactsPerf : stats.plays_expected > 0 ? playsPerf : timePerf,
    );
    const valueDelivered = campaignValues?.delivered ?? 0;
    const valueExpected = campaignValues?.expected ?? 0;
    const valuePerf = valueExpected > 0 ? (valueDelivered / valueExpected) * 100 : 0;

    const countFields = [
        PCAReportCountField.InSchedule,
        PCAReportCountField.OutSchedule,
        PCAReportCountField.UnBooked,
    ];
    const inScheduleLabel = t('Modules.Main.Campaigns.Overview.Reports.inScheduleLabel');
    const outScheduleLabel = t('Modules.Main.Campaigns.Overview.Reports.outScheduleLabel');
    const unbookedLabel = t('Modules.Main.Campaigns.Overview.Reports.unbookedLabel');
    const performanceLabel = t('Modules.Main.Campaigns.Overview.Analytics.performance');
    const impactsTooltipConfig: MetricChartTooltipConfig = {
        metric: PCAReportMetric.Impacts,
        expectedLabel: t('Modules.Main.Campaigns.Overview.Reports.impacts.expectedLabel'),
        percentageLabel: t('Modules.Main.Campaigns.Overview.Reports.impacts.percentageLabel'),
        inScheduleLabel,
        outScheduleLabel,
        unbookedLabel,
        performanceLabel,
        countFields,
        hidePercentage: false,
        showPerformance: false,
        counts: {
            inSchedule: stats.impacts_in_schedule ?? 0,
            outSchedule: stats.impacts_out_schedule ?? 0,
            unbooked: stats.impacts_unbooked ?? 0,
        },
    };
    const playsTooltipConfig: MetricChartTooltipConfig = {
        metric: PCAReportMetric.Plays,
        expectedLabel: t('Modules.Main.Campaigns.Overview.Reports.plays.expectedLabel'),
        percentageLabel: t('Modules.Main.Campaigns.Overview.Reports.plays.percentageLabel'),
        inScheduleLabel,
        outScheduleLabel,
        unbookedLabel,
        performanceLabel,
        countFields,
        hidePercentage: false,
        showPerformance: false,
        counts: {
            inSchedule: stats.plays_in_schedule ?? 0,
            outSchedule: stats.plays_out_schedule ?? 0,
            unbooked: stats.plays_unbooked ?? 0,
        },
    };

    // Full breakdown rows per box — used by both the inline "show all data" render and the dialog.
    const buildLinearRows = (metric: TileMetric | null): MetricDetailsRow[] => {
        if (metric === 'impacts') {
            const variance = (stats.impacts ?? 0) - stats.impacts_expected;
            return [
                {
                    label: 'Performance',
                    value: stats.impacts_expected > 0 ? `${Math.round(impactsPerf)}%` : '—',
                    kind: 'performance',
                    tone: perfTone(impactsPerf),
                },
                { label: 'Expected', value: formatNumber(stats.impacts_expected ?? 0) },
                { label: 'Total', value: formatNumber(stats.impacts ?? 0) },
                {
                    label: 'Variance',
                    value: `${variance >= 0 ? '+' : ''}${formatNumber(variance)}`,
                    kind: 'variance',
                    tone: varianceTone(variance),
                },
                { label: 'In Schedule', value: formatNumber(stats.impacts_in_schedule ?? 0) },
                { label: 'Out of Schedule', value: formatNumber(stats.impacts_out_schedule ?? 0) },
                { label: 'Unbooked', value: formatNumber(stats.impacts_unbooked ?? 0) },
            ];
        }
        if (metric === 'plays') {
            const variance = (stats.plays ?? 0) - stats.plays_expected;
            return [
                {
                    label: 'Performance',
                    value: stats.plays_expected > 0 ? `${Math.round(playsPerf)}%` : '—',
                    kind: 'performance',
                    tone: perfTone(playsPerf),
                },
                { label: 'Expected', value: formatNumberWhole(stats.plays_expected ?? 0) },
                { label: 'Total', value: formatNumberWhole(stats.plays ?? 0) },
                {
                    label: 'Variance',
                    value: `${variance >= 0 ? '+' : ''}${formatNumberWhole(variance)}`,
                    kind: 'variance',
                    tone: varianceTone(variance),
                },
                { label: 'In Schedule', value: formatNumberWhole(stats.plays_in_schedule ?? 0) },
                {
                    label: 'Out of Schedule',
                    value: formatNumberWhole(stats.plays_out_schedule ?? 0),
                },
                { label: 'Unbooked', value: formatNumberWhole(stats.plays_unbooked ?? 0) },
            ];
        }
        if (metric === 'time') {
            const variance = (stats.time ?? 0) - stats.time_expected;
            return [
                {
                    label: 'Performance',
                    value: stats.time_expected > 0 ? `${Math.round(timePerf)}%` : '—',
                    kind: 'performance',
                    tone: perfTone(timePerf),
                },
                {
                    label: 'Expected',
                    value: (
                        <HoursMins
                            seconds={stats.time_expected ?? 0}
                            variant="body2"
                            alwaysShowMinutes
                        />
                    ),
                },
                {
                    label: 'Total',
                    value: (
                        <HoursMins seconds={stats.time ?? 0} variant="body2" alwaysShowMinutes />
                    ),
                },
                {
                    label: 'Variance',
                    value: <HoursMins seconds={variance} variant="body2" alwaysShowMinutes />,
                    kind: 'variance',
                    tone: varianceTone(variance),
                },
                {
                    label: 'In Schedule',
                    value: (
                        <HoursMins
                            seconds={stats.time_in_schedule ?? 0}
                            variant="body2"
                            alwaysShowMinutes
                        />
                    ),
                },
                {
                    label: 'Out of Schedule',
                    value: (
                        <HoursMins
                            seconds={stats.time_out_schedule ?? 0}
                            variant="body2"
                            alwaysShowMinutes
                        />
                    ),
                },
                {
                    label: 'Unbooked',
                    value: (
                        <HoursMins
                            seconds={stats.time_unbooked ?? 0}
                            variant="body2"
                            alwaysShowMinutes
                        />
                    ),
                },
            ];
        }
        if (metric === 'spend') {
            const variance = valueDelivered - valueExpected;
            return [
                {
                    label: 'Performance',
                    value: valueExpected > 0 ? `${Math.round(valuePerf)}%` : '—',
                    kind: 'performance',
                    tone: perfTone(valuePerf),
                },
                {
                    label: 'Expected',
                    value: `${campaignValues.currencySign}${formatNumber(valueExpected)}`,
                },
                {
                    label: 'Total',
                    value: `${campaignValues.currencySign}${formatNumber(valueDelivered)}`,
                },
                {
                    label: 'Variance',
                    value: `${variance >= 0 ? '+' : ''}${campaignValues.currencySign}${formatNumber(
                        variance,
                    )}`,
                    kind: 'variance',
                    tone: varianceTone(variance),
                },
            ];
        }
        return [];
    };
    const impactsBox = getTopBoxConfig(topBoxConfigs, 'impacts');
    const playsBox = getTopBoxConfig(topBoxConfigs, 'plays');
    const visibleTileCount =
        (impactsBox.hidden ? 0 : 1) + (playsBox.hidden ? 0 : 1) + (campaignValues?.show ? 1 : 0);
    const cols = Math.max(1, visibleTileCount);

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: `repeat(${Math.min(2, cols)}, 1fr)`,
                    md: `repeat(${cols}, 1fr)`,
                },
            }}
        >
            {!impactsBox.hidden && (
                <CompactTile
                    title={t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')}
                    value={formatCompact(stats.impacts ?? 0)}
                    valueLabel={t('Modules.Main.Campaigns.Overview.Analytics.actual')}
                    performanceFirst
                    performance={impactsPerf}
                    hasExpected={stats.impacts_expected > 0}
                    expectedDisplay={
                        stats.impacts_expected > 0 ? formatCompact(stats.impacts_expected) : null
                    }
                    varianceDisplay={
                        stats.impacts_expected > 0
                            ? `${
                                  (stats.impacts ?? 0) - stats.impacts_expected >= 0 ? '+' : ''
                              }${formatCompact((stats.impacts ?? 0) - stats.impacts_expected)}`
                            : null
                    }
                    varianceSign={
                        stats.impacts_expected > 0
                            ? (stats.impacts ?? 0) - stats.impacts_expected > 0
                                ? 'positive'
                                : (stats.impacts ?? 0) - stats.impacts_expected < 0
                                ? 'negative'
                                : 'neutral'
                            : 'neutral'
                    }
                    sparklinePoints={impactsPoints}
                    chartTooltipConfig={impactsTooltipConfig}
                    chartControls={controlsFor('impacts')}
                    onChartControlsChange={onControlsFor('impacts')}
                    showGraph={impactsBox.showGraph}
                    showAllData={impactsBox.showAllData}
                    detailRows={buildLinearRows('impacts')}
                    onViewDetails={() => setOpenMetric('impacts')}
                />
            )}
            {!playsBox.hidden && (
                <CompactTile
                    title={t(playsTitle)}
                    value={formatCompact(stats.plays ?? 0)}
                    valueLabel={t('Modules.Main.Campaigns.Overview.Analytics.actual')}
                    performanceFirst
                    performance={playsPerf}
                    hasExpected={stats.plays_expected > 0}
                    expectedDisplay={
                        stats.plays_expected > 0 ? formatCompact(stats.plays_expected) : null
                    }
                    varianceDisplay={
                        stats.plays_expected > 0
                            ? `${
                                  (stats.plays ?? 0) - stats.plays_expected >= 0 ? '+' : ''
                              }${formatCompact((stats.plays ?? 0) - stats.plays_expected)}`
                            : null
                    }
                    varianceSign={
                        stats.plays_expected > 0
                            ? (stats.plays ?? 0) - stats.plays_expected > 0
                                ? 'positive'
                                : (stats.plays ?? 0) - stats.plays_expected < 0
                                ? 'negative'
                                : 'neutral'
                            : 'neutral'
                    }
                    sparklinePoints={playsPoints}
                    chartActualLabel={t(
                        'Modules.Main.Campaigns.Overview.Reports.plays.actualLabel',
                    )}
                    chartExpectedLabel={t(
                        'Modules.Main.Campaigns.Overview.Reports.plays.expectedLabel',
                    )}
                    chartTooltipConfig={playsTooltipConfig}
                    chartControls={controlsFor('plays')}
                    onChartControlsChange={onControlsFor('plays')}
                    showGraph={playsBox.showGraph}
                    showAllData={playsBox.showAllData}
                    detailRows={buildLinearRows('plays')}
                    onViewDetails={() => setOpenMetric('plays')}
                />
            )}
            {campaignValues?.show && (
                <CompactTile
                    title={t('Modules.Main.Campaigns.Overview.Analytics.valueTitle')}
                    value={`${campaignValues.currencySign}${formatCompact(valueDelivered)}`}
                    performance={valuePerf}
                    hasExpected={valueExpected > 0}
                    expectedLabel={t('Modules.Main.Campaigns.Overview.Analytics.expectedValue')}
                    expectedDisplay={
                        valueExpected > 0
                            ? `${campaignValues.currencySign}${formatCompact(valueExpected)}`
                            : null
                    }
                    actualLabel={t(
                        'Modules.Main.Campaigns.Overview.Analytics.actualValueDelivered',
                    )}
                    actualDisplay={`${campaignValues.currencySign}${formatCompact(valueDelivered)}`}
                    varianceDisplay={
                        valueExpected > 0
                            ? `${valueDelivered - valueExpected >= 0 ? '+' : ''}${
                                  campaignValues.currencySign
                              }${formatCompact(valueDelivered - valueExpected)}`
                            : null
                    }
                    varianceSign={
                        valueExpected > 0
                            ? valueDelivered - valueExpected > 0
                                ? 'positive'
                                : valueDelivered - valueExpected < 0
                                ? 'negative'
                                : 'neutral'
                            : 'neutral'
                    }
                />
            )}
            <MetricDetailsDialog
                open={openMetric != null}
                onClose={() => setOpenMetric(null)}
                title={(() => {
                    if (openMetric === 'impacts')
                        return t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle');
                    if (openMetric === 'plays') return t(playsTitle);
                    if (openMetric === 'time')
                        return t('Modules.Main.Campaigns.Overview.Analytics.timeTitle');
                    return 'Value';
                })()}
                chartData={(() => {
                    if (!dailyCumulative || dailyCumulative.length === 0) return undefined;
                    if (openMetric === 'impacts')
                        return dailyCumulative.map(d => ({
                            date: d.date,
                            actual: d.impacts,
                            expected: d.impactsExpected,
                        }));
                    if (openMetric === 'plays')
                        return dailyCumulative.map(d => ({
                            date: d.date,
                            actual: d.plays,
                            expected: d.playsExpected,
                        }));
                    if (openMetric === 'time')
                        return dailyCumulative.map(d => ({
                            date: d.date,
                            actual: d.time,
                            expected: d.timeExpected,
                        }));
                    return undefined;
                })()}
                chartActualLabel={
                    openMetric === 'plays'
                        ? t('Modules.Main.Campaigns.Overview.Reports.plays.actualLabel')
                        : undefined
                }
                chartExpectedLabel={
                    openMetric === 'plays'
                        ? t('Modules.Main.Campaigns.Overview.Reports.plays.expectedLabel')
                        : undefined
                }
                chartTooltipConfig={
                    openMetric === 'plays' ? playsTooltipConfig : impactsTooltipConfig
                }
                controls={controlsFor(openMetric ?? 'impacts')}
                onControlsChange={onControlsFor(openMetric ?? 'impacts')}
                rows={buildLinearRows(openMetric)}
            />
        </Box>
    );
};

export default PCATotalsByMetric;
